import type { Column, Item } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useCallback, useMemo, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import { ContactPointCodes, RelationshipCodes } from '../../../lib/Codes'
import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { capitalize } from '../../igao/utils'
import { Copyable } from '../../layout/sidebar'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface MobileInvitation extends Item {
  patient_id: string
  patient_name: string
  platform: 'ios' | 'android'
  phone_number: string
  receiver_name: string
  message: string
  sent_by: string
  sent_by_name?: string
  sent_by_email?: string
  client_name?: string
  status: 'queued' | 'sent' | 'failed' | 'delivered'
  error_message?: string
  created_at: string
  updated_at: string
  relationship: string
}

type QueryKey = [
  'mobile-invites',
  SearchParams
]

interface QueryData {
  data: MobileInvitation[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string
  filters: {
    platform: Array<'ios' | 'android'>
    status: Array<'queued' | 'sent' | 'failed' | 'delivered'>
  }
  sort: Sort
  pagination: Pagination
}

const sortKeys = [
  'created_at',
  'patient_name',
  'platform',
  'status'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

const initialState: State = {
  searchParams: {
    query: '',
    filters: {
      platform: [],
      status: []
    },
    sort: {
      field: 'created_at',
      direction: 'desc'
    },
    pagination: {
      page: 0,
      pageSize: 25
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: 0,
            pageSize: action.payload
          }
        }
      }
    }

    default:
      return state
  }
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.platform.length === initialState.searchParams.filters.platform.length &&
    searchParams.filters.status.length === initialState.searchParams.filters.status.length &&
    searchParams.filters.platform.every(v => initialState.searchParams.filters.platform.includes(v)) &&
    searchParams.filters.status.every(v => initialState.searchParams.filters.status.includes(v))
  )
}

async function fetchMobileInvitations (searchParams: SearchParams): Promise<QueryData> {
  const { query, filters, sort, pagination } = searchParams
  const normalizedQuery = query.trim().toLowerCase()

  const sp = new URLSearchParams([
    [ 'page', pagination.page.toString() ],
    [ 'pageSize', pagination.pageSize.toString() ],
    [ 'sortBy', sort.field ],
    [ 'sortOrder', sort.direction ],
    ...(normalizedQuery.length > 0 ? [[ 'query', normalizedQuery ]] : [])
  ])

  // For now, convert arrays to single values to work with current backend
  if (filters.platform.length === 1 && filters.platform[0]) {
    sp.append('platform', filters.platform[0])
  } else if (filters.platform.length === 0) {
    sp.append('platform', 'all')
  }

  if (filters.status.length === 1 && filters.status[0]) {
    sp.append('status', filters.status[0])
  } else if (filters.status.length === 0) {
    sp.append('status', 'all')
  }

  try {
    const response = await http.getRequest(`/admin/mobile-invites?${sp.toString()}`)
    return response
  } catch (error) {
    console.error('Failed to fetch mobile invitations:', error)
    throw error
  }
}

export function MobileInvites (): JSX.Element {
  const [ state, dispatch ] = useReducer(reducer, initialState)
  const [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  const query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'mobile-invites',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      const [ , searchParams ] = queryKey
      return await fetchMobileInvitations(searchParams)
    },

    placeholderData: keepPreviousData
  })

  const columns = useMemo<Array<Column<MobileInvitation>>>(() => [
    {
      name: 'created_at',
      header: 'Time',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (invitation) {
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(invitation.created_at)}
          </span>
        )
      }
    },
    {
      name: 'patient_name',
      header: 'Patient',
      isSortable: true,
      getValue (invitation) {
        const clientName = invitation.client_name ?? 'compassion'
        return (
          <Link
            to={`/patient/${clientName}/${invitation.patient_id}/assessment`}
            className={styles.link}
          >
            {invitation.patient_name}
          </Link>
        )
      }
    },
    {
      name: 'receiver_name',
      header: 'Recipient',
      getValue (invitation) {
        let relationship

        if (invitation.relationship === 'patient') {
          relationship = 'Patient'
        } else {
          relationship = capitalize(RelationshipCodes.find((rc: {
            value: string
          }) => rc.value === invitation.relationship)?.display)
        }

        return (
          <span>
            {invitation.receiver_name}{' '}
            <span
              className='text-zinc-500 text-xs font-bold'
            >
              {relationship}
            </span>
          </span>
        )
      }
    },
    {
      name: 'phone_number',
      header: 'Phone',
      getValue (invitation) {
        let phoneType = ContactPointCodes.contactPointUse.find((cpu) => cpu.code === invitation.phone_type)?.label
        return (
          <Copyable value={invitation.phone_number}>{invitation.phone_number}{' '}
            <span
              className='text-zinc-500 text-xs font-bold'
            >
              {phoneType}
            </span>
          </Copyable>
        )
      }
    },
    {
      name: 'platform',
      header: 'Platform',
      isSortable: true,
      getValue (invitation) {
        return (
          invitation.platform === 'android'
            ? (
              <span
                className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-xs bg-[#e6f7ef] text-[#16794f]'
              >
                <i className='fa fa-android' /> Android
              </span>
            )
            : (
              <span
                className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-xs bg-[#e6f3ff] text-[#0b62a4]'
              >
                <i className='fa fa-apple' /> iOS
              </span>
            )
        )
      }
    },
    {
      name: 'sent_by_name',
      header: 'Sent By',
      getValue (invitation) {
        // Display format: "Name <email>" or fallback to System
        if (invitation.sent_by_name && invitation.sent_by_email) {
          return (
            <span>{invitation.sent_by_name} &lt;{invitation.sent_by_email}&gt;</span>
          )
        }
        return (
          <span>{invitation.sent_by_name ?? 'System'}</span>
        )
      }
    }
  ], [])

  const filters = useMemo(() => {
    return [
      {
        name: 'platform',
        value: state.searchParams.filters.platform,
        options: [
          { value: 'ios', label: 'iOS' },
          { value: 'android', label: 'Android' }
        ]
      },
      {
        name: 'status',
        value: state.searchParams.filters.status,
        options: [
          { value: 'queued', label: 'Queued' },
          { value: 'sent', label: 'Sent' },
          { value: 'failed', label: 'Failed' },
          { value: 'delivered', label: 'Delivered' }
        ]
      }
    ]
  }, [ state.searchParams.filters ])

  const handleFilterChange = useCallback(<T extends keyof MobileInvitation>(name: T, value: Array<MobileInvitation[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])

  const handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  const handleSortChange = useCallback((field: keyof MobileInvitation, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  const handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  const handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  const pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Mobile Invites</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by patient name, phone number, sent by name, email, and status'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No mobile invites found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(invitation) => {
          return (
            <div className={styles.expandedContent}>
              <div>
                <strong>Message:</strong>
                <p>{String(invitation.message ?? '')}</p>
              </div>
              {invitation.error_message && (
                <div className={styles.errorMessage}>
                  <strong>Error:</strong>
                  <p>{String(invitation.error_message)}</p>
                </div>
              )}
              <div className={styles.metadata}>
                <span>ID: {invitation.id}</span>
                <span>Updated: {format(new Date(invitation.updated_at), 'MMM dd, yyyy HH:mm:ss')}</span>
              </div>
            </div>
          )
        }}
      />
    </div>
  )
}
